import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';

import { formatDateShort, activeDateLang } from 'lib/dateFormat';

export interface DatePickerProps {
  /** Selected date as an ISO `YYYY-MM-DD` string (or empty when unset). */
  value?: string;
  /** Called with the new `YYYY-MM-DD` string (empty string when cleared). */
  onChange: (value: string) => void;
  /** Inclusive lower bound as `YYYY-MM-DD`. */
  min?: string;
  /** Inclusive upper bound as `YYYY-MM-DD`. */
  max?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  ariaLabel?: string;
}

const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
const toISO = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Parse `YYYY-MM-DD` in local time (avoids the UTC shift of `new Date(string)`). */
function parseISO(value?: string): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

const dayStart = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const addMonths = (d: Date, n: number): Date => new Date(d.getFullYear(), d.getMonth() + n, 1);
/** Monday-first weekday index (0 = Monday … 6 = Sunday) — matches uz/ru calendars. */
const mondayIndex = (d: Date): number => (d.getDay() + 6) % 7;

export function DatePicker({
  value,
  onChange,
  min,
  max,
  placeholder,
  disabled,
  id,
  className,
  ariaLabel,
}: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  const selected = React.useMemo(() => parseISO(value), [value]);
  const minDate = React.useMemo(() => parseISO(min), [min]);
  const maxDate = React.useMemo(() => parseISO(max), [max]);

  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState<Date>(() => selected ?? new Date());

  // Jump the visible month to the selected date (or today) each time it opens.
  React.useEffect(() => {
    if (open) setView(selected ?? new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const monthLabel = React.useMemo(() => {
    const lang = activeDateLang();
    const months: Record<string, string[]> = {
      en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
      uz: ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun', 'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'],
    };
    const list = months[lang] || months.en;
    return `${list[view.getMonth()]} ${view.getFullYear()}`;
  }, [view]);

  const weekdayLabels = React.useMemo(() => {
    const lang = activeDateLang();
    const days: Record<string, string[]> = {
      en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
      ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
      uz: ['Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sha', 'Ya'],
    };
    return days[lang] || days.en;
  }, []);

  const triggerLabel = React.useMemo(
    () => (selected ? formatDateShort(selected) : ''),
    [selected],
  );

  // 6 rows × 7 days keeps the popover height stable across months.
  const days = React.useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const start = new Date(view.getFullYear(), view.getMonth(), 1 - mondayIndex(first));
    return Array.from(
      { length: 42 },
      (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i),
    );
  }, [view]);

  const isDisabledDay = (day: Date): boolean => {
    const ds = dayStart(day);
    if (minDate && ds < dayStart(minDate)) return true;
    if (maxDate && ds > dayStart(maxDate)) return true;
    return false;
  };

  const handleSelect = (day: Date): void => {
    if (isDisabledDay(day)) return;
    onChange(toISO(day));
    setOpen(false);
  };

  const today = new Date();

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild disabled={disabled}>
        <button
          type="button"
          id={id}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-card px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className={cn('flex-1 truncate text-left', !selected && 'text-muted-foreground')}>
            {triggerLabel || placeholder || t('select_date')}
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[17rem] rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-md"
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              aria-label={t('previous')}
              onClick={() => setView(addMonths(view, -1))}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold capitalize">{monthLabel}</span>
            <button
              type="button"
              aria-label={t('next')}
              onClick={() => setView(addMonths(view, 1))}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {weekdayLabels.map((wd) => (
              <div key={wd} className="py-1 text-center text-[11px] font-medium uppercase text-muted-foreground">
                {wd}
              </div>
            ))}
            {days.map((day) => {
              const inMonth = day.getMonth() === view.getMonth();
              const disabledDay = isDisabledDay(day);
              const isSelected = !!selected && isSameDay(day, selected);
              const isToday = isSameDay(day, today);
              return (
                <button
                  key={toISO(day)}
                  type="button"
                  disabled={disabledDay}
                  onClick={() => handleSelect(day)}
                  aria-label={new Intl.DateTimeFormat(locale, { dateStyle: 'full' }).format(day)}
                  aria-pressed={isSelected}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-md text-sm transition-colors',
                    !inMonth && 'text-muted-foreground/40',
                    !disabledDay && !isSelected && 'hover:bg-accent',
                    isToday && !isSelected && 'font-semibold text-foreground ring-1 ring-inset ring-foreground/20',
                    isSelected && 'bg-foreground text-background hover:bg-foreground',
                    disabledDay && 'cursor-not-allowed opacity-30',
                  )}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default DatePicker;
